import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import {
  Loader2,
  CalendarX,
  ArrowLeft,
  ExternalLink,
  Hotel,
  Calendar,
  ShieldCheck,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ForfeoCheckoutNetworkPanel from '../components/network/ForfeoCheckoutNetworkPanel';
import ReserveUserExperience from '../components/reserve/ReserveUserExperience';
import { isForfeoReserveUserExperienceEnabled } from '../lib/forfeoReserveUserExperienceClient';
import Cal, { getCalApi } from '@calcom/embed-react';

// Types locaux pour éviter les dépendances manquantes
type BusinessBookingSettings = {
  is_booking_enabled: boolean;
  cal_username?: string;
  cal_event_slug?: string;
  qlo_endpoint_url?: string;
  business_id: string;
};

const BookingPage = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const reserveEntry = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const rawPartySize = Number(params.get('partySize') || '2');
    return {
      preferredAccommodationId: params.get('legacyRoomId') || undefined,
      initialPartySize: Number.isFinite(rawPartySize) ? Math.max(1, Math.min(20, Math.floor(rawPartySize))) : 2,
      initialDate: params.get('date') || params.get('check_in') || undefined,
      initialEndDate: params.get('endDate') || params.get('check_out') || undefined,
    };
  }, [location.search]);

  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState<BusinessBookingSettings | null>(null);

  const isBookingEnabled = import.meta.env.VITE_ENABLE_BOOKING === 'true';
  const isReserveUserExperienceEnabled = isForfeoReserveUserExperienceEnabled();

  // Hauteur embed responsive (corrige le “pas de scroll” sur petits écrans)
  const calEmbedStyle = useMemo<React.CSSProperties>(() => {
    return {
      width: '100%',
      height: 'calc(100dvh - 260px)', // Header + paddings + titre
      minHeight: '640px',
      overflow: 'hidden',
    };
  }, []);

  useEffect(() => {
    // 4R-G remplace progressivement le moteur historique derrière un flag explicite.
    if (isReserveUserExperienceEnabled) {
      setLoading(false);
      return;
    }
    // Si les deux moteurs sont désactivés, retour accueil.
    if (!isBookingEnabled) {
      navigate('/');
      return;
    }
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId]);

  // Cal.com integration (uniquement si cal_username présent)
  useEffect(() => {
    let mounted = true;

    const initCal = async () => {
      if (!settings?.cal_username) return;

      try {
        const cal = await getCalApi();

        // UI config
        cal('ui', {
          styles: { branding: { brandColor: '#000000' } },
          hideEventTypeDetails: false,
          layout: 'month_view',
        });

        // Event callback : on redirige vers BookingSuccess
        // IMPORTANT : BookingSuccess attend un paymentRef (session_id/payment_intent)
        // donc on envoie un session_id “cal_...”
        cal('on', {
          action: 'bookingSuccessful',
          callback: (e: any) => {
            if (!mounted) return;

            // Selon versions, les infos peuvent être dans e.detail.data
            const data = e?.detail?.data || e?.data || {};
            const bookingUid =
              data?.uid ||
              data?.bookingUid ||
              data?.booking?.uid ||
              data?.id ||
              `${Date.now()}`;

            // Date si disponible (startTime/slot)
            const start =
              data?.startTime ||
              data?.start ||
              data?.booking?.startTime ||
              data?.booking?.start ||
              null;

            // ✅ RÉCUPÉRATION DU CODE AFFILIÉ (Moteur d'Attribution)
            const affiliateCode = localStorage.getItem('forfeo_affiliate_code');

            const qs = new URLSearchParams();
            if (businessId) qs.set('business_id', businessId);
            qs.set('session_id', `cal_${String(bookingUid)}`);
            if (start) qs.set('date', String(start));

            // ✅ Transmission du code affilié à la page de succès
            if (affiliateCode) qs.set('affiliate_code', affiliateCode);

            navigate(`/booking/success?${qs.toString()}`);
          },
        });
      } catch (err) {
        console.error('Erreur init Cal.com:', err);
      }
    };

    initCal();

    return () => {
      mounted = false;
    };
  }, [settings?.cal_username, businessId, navigate]);

  const fetchSettings = async () => {
    if (!businessId) return;
    setLoading(true);

    try {
      const { data, error } = await supabase
        .from('business_booking_settings')
        .select('*')
        .eq('business_id', businessId)
        .maybeSingle();

      if (error) console.error('Erreur settings:', error);
      setSettings(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleLodgingRedirect = () => {
    if (settings?.qlo_endpoint_url) {
      // ✅ Envoi du code affilié vers le partenaire externe (QloApps)
      // Si QloApps supporte un paramètre d'URL pour le tracking (ex: ?ref=...), on peut l'ajouter ici.
      // Pour l'instant, on l'ajoute comme paramètre générique 'ref'.
      const affiliateCode = localStorage.getItem('forfeo_affiliate_code');
      let targetUrl = settings.qlo_endpoint_url;

      if (affiliateCode) {
        const separator = targetUrl.includes('?') ? '&' : '?';
        targetUrl = `${targetUrl}${separator}ref=${affiliateCode}`;
      }

      window.open(targetUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const openCalInNewTab = () => {
    if (!settings?.cal_username) return;
    const slug = settings.cal_event_slug ? `/${settings.cal_event_slug}` : '';
    const url = `https://cal.com/${settings.cal_username}${slug}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  if (!isBookingEnabled && !isReserveUserExperienceEnabled) return null;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col font-sans">
      <Header />

      {/* IMPORTANT SCROLL FIX :
          - flex-grow + overflow-y-auto permet le défilement même si un parent impose des hauteurs
          - pb-24 assure que rien n’est coupé en bas (mobile) */}
      <main className="flex-grow overflow-y-auto">
        <div className="container mx-auto px-4 py-8 pt-24 pb-24">
          {isReserveUserExperienceEnabled && businessId ? (
            <div className="max-w-5xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 text-slate-500 font-bold text-xs uppercase tracking-widest hover:text-slate-900 mb-6 transition"
              >
                <ArrowLeft size={16} /> Retour à la fiche
              </button>
              <ReserveUserExperience
                businessId={businessId}
                preferredAccommodationId={reserveEntry.preferredAccommodationId}
                initialPartySize={reserveEntry.initialPartySize}
                initialDate={reserveEntry.initialDate}
                initialEndDate={reserveEntry.initialEndDate}
              />
            </div>
          ) : loading ? (
            <div className="flex justify-center items-center h-64">
              <Loader2 className="animate-spin text-blue-600" size={40} />
            </div>
          ) : !settings || !settings.is_booking_enabled ? (
            <div className="max-w-2xl mx-auto bg-white p-10 md:p-12 rounded-3xl text-center shadow-sm border border-slate-100 mt-10">
              <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CalendarX size={32} className="text-slate-400" />
              </div>
              <h2 className="text-2xl font-black text-slate-900 uppercase tracking-tight">
                Réservation non disponible
              </h2>
              <p className="text-slate-500 mt-2 text-sm font-medium">
                Ce commerce n&apos;a pas encore activé la réservation en ligne.
              </p>
              <button
                onClick={() => navigate(-1)}
                className="mt-8 bg-slate-900 text-white px-6 py-3 rounded-xl font-bold uppercase tracking-widest text-xs hover:bg-slate-800 transition"
              >
                Retour
              </button>
            </div>
          ) : (
            <div className="max-w-5xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 text-slate-500 font-bold text-xs uppercase tracking-widest hover:text-slate-900 mb-6 transition"
              >
                <ArrowLeft size={16} /> Retour à la fiche
              </button>

              <div className="mb-6">
                <ForfeoCheckoutNetworkPanel variant="booking" />
              </div>

              {/* CAS 1 : CAL.COM (Pour services/activités) */}
              {settings.cal_username ? (
                <div className="bg-white rounded-3xl shadow-xl border border-slate-100 overflow-hidden">
                  <div className="bg-slate-900 p-6 text-white text-center">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <Calendar size={20} className="text-purple-400" />
                      <h1 className="text-2xl font-black uppercase tracking-tighter">
                        Réserver un créneau
                      </h1>
                    </div>
                    <p className="text-slate-400 text-xs font-bold">
                      Sélectionnez votre date et heure ci-dessous
                    </p>
                  </div>

                  {/* CTA “ouvrir dans un nouvel onglet” (utile mobile si embed capricieux) */}
                  <div className="px-4 md:px-8 pt-4 flex justify-end">
                    <button
                      onClick={openCalInNewTab}
                      className="text-xs font-black uppercase tracking-widest text-slate-600 hover:text-slate-900 flex items-center gap-2"
                    >
                      Ouvrir sur Cal.com <ExternalLink size={14} />
                    </button>
                  </div>

                  <div className="p-4 md:p-8 bg-white">
                    <Cal
                      calLink={`${settings.cal_username}/${settings.cal_event_slug || ''}`}
                      style={calEmbedStyle}
                      config={{ layout: 'month_view' }}
                    />
                  </div>

                  <div className="px-6 md:px-8 pb-6">
                    <p className="text-xs text-slate-400 font-medium flex items-center justify-center gap-1">
                      <ShieldCheck size={12} /> Réservation sécurisée via le partenaire officiel.
                    </p>
                  </div>
                </div>
              ) : settings.qlo_endpoint_url ? (
                /* CAS 2 : HÉBERGEMENT (QloApps / Lien Externe) */
                <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-slate-100 max-w-3xl mx-auto">
                  <div className="bg-blue-600 p-8 md:p-10 text-white text-center relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-10 rounded-full blur-3xl -mr-20 -mt-20"></div>
                    <Hotel size={48} className="mx-auto mb-4 text-blue-200" />
                    <h1 className="text-3xl font-black uppercase tracking-tighter mb-2">
                      Réserver un Séjour
                    </h1>
                    <p className="text-blue-100 font-medium">
                      Vérifiez les disponibilités directement auprès de l&apos;établissement.
                    </p>
                  </div>

                  <div className="p-8 md:p-10 text-center">
                    <p className="text-slate-600 mb-8 text-sm leading-relaxed">
                      Pour garantir le meilleur tarif et voir les disponibilités en temps réel,
                      nous vous redirigeons vers le moteur de réservation sécurisé de l&apos;hôtel.
                    </p>

                    <button
                      onClick={handleLodgingRedirect}
                      className="inline-flex items-center gap-3 bg-blue-600 text-white px-8 py-4 rounded-xl font-black uppercase tracking-widest text-sm hover:bg-blue-700 transition shadow-lg hover:-translate-y-1"
                    >
                      Voir les disponibilités <ExternalLink size={16} />
                    </button>

                    <p className="mt-6 text-xs text-slate-400 font-medium flex items-center justify-center gap-1">
                      <ShieldCheck size={12} /> Réservation sécurisée via le partenaire officiel.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="text-center p-10 bg-white rounded-3xl border border-slate-100">
                  Configuration technique incomplète pour ce partenaire.
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default BookingPage;
